import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../common/context/AuthContext';
import PinVerificationModal from './PinVerificationModal';
import PermissionDenied from '../../../common/components/PermissionDenied';
import { API_URL } from '../../../api/config';
import { Lock } from 'lucide-react';

const ProtectedRoute = ({ 
  children, 
  requiredModule = null, 
  requireAdmin = false 
}) => {
  const { userToken: token, userRole } = useAuth();
  const [showPinModal, setShowPinModal] = useState(false);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [pinProtectionEnabled, setPinProtectionEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Session durations in milliseconds
  const SESSION_DURATION = {
    admin: 30 * 60 * 1000, // 30 minutes for admin
    manager: 15 * 60 * 1000 // 15 minutes for manager
  };

  useEffect(() => {
    checkAccess();
  }, [requiredModule, userRole]);

  useEffect(() => {
    // Check for existing valid session on mount
    if (isPinVerified) {
      checkSession();
      
      // Set up session expiry timer
      const sessionTimer = setInterval(() => {
        checkSession();
      }, 60000); // Check every minute

      return () => clearInterval(sessionTimer);
    }
  }, [isPinVerified, userRole]);

  const checkSession = () => {
    const sessionKey = `pin_session_${requiredModule || 'default'}`;
    const sessionData = sessionStorage.getItem(sessionKey);

    if (sessionData) {
      try {
        const { role, timestamp } = JSON.parse(sessionData);
        const now = Date.now();
        const elapsed = now - timestamp;
        const maxDuration = SESSION_DURATION[role] || SESSION_DURATION.manager;

        if (elapsed < maxDuration) {
          // Session still valid
          setIsPinVerified(true);
          setShowPinModal(false);
          return true;
        } else {
          // Session expired
          clearSession();
          setSessionExpired(true);
          setIsPinVerified(false);
          setShowPinModal(true);
          return false;
        }
      } catch (error) {
        console.error('Error parsing session data:', error);
        clearSession();
      }
    }
    return false;
  };

  const setSession = (role) => {
    const sessionKey = `pin_session_${requiredModule || 'default'}`;
    const sessionData = {
      role,
      timestamp: Date.now(),
      module: requiredModule
    };
    sessionStorage.setItem(sessionKey, JSON.stringify(sessionData));
  };

  const clearSession = () => {
    const sessionKey = `pin_session_${requiredModule || 'default'}`;
    sessionStorage.removeItem(sessionKey);
  };

  const checkAccess = async () => {
    // First check if there's a valid session
    if (checkSession()) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/pin-settings/check-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          module: requiredModule,
          requireAdmin 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setHasAccess(data.hasAccess);
        setPinProtectionEnabled(data.pinProtectionEnabled);
        
        if (data.hasAccess && data.pinProtectionEnabled && !isPinVerified) {
          setShowPinModal(true);
        } else if (data.hasAccess && !data.pinProtectionEnabled) {
          setIsPinVerified(true);
        }
      } else {
        if (response.status === 404 || response.status === 500) {
          setHasAccess(true);
          setPinProtectionEnabled(false);
          setIsPinVerified(true);
        } else {
          setHasAccess(false);
        }
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(true);
      setPinProtectionEnabled(false);
      setIsPinVerified(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePinVerify = async (pin, role) => {
    try {
      const response = await fetch(`${API_URL}/pin-settings/verify-pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pin, role })
      });

      const data = await response.json();
      
      if (response.ok && data.valid) {
        setIsPinVerified(true);
        setShowPinModal(false);
        setSessionExpired(false);
        
        // Set session with role
        setSession(role);
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    }
  };

  const handleModalClose = () => {
    clearSession();
    window.history.back();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm font-semibold text-slate-600">Checking access...</p>
        </div>
      </div>
    );
  }

  if (hasAccess === false) {
    return (
      <PermissionDenied
        variant="fullPage"
        title="We are Sorry..."
        message="The page or report you are trying to access has restricted access. Please refer to your system administrator for permissions."
        onGoBack={() => window.history.back()}
      />
    );
  }

  if (pinProtectionEnabled && !isPinVerified) {
    return (
      <>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="text-blue-600" size={32} />
            </div>
            <p className="text-sm font-semibold text-slate-600">
              {sessionExpired ? 'Session expired. Please verify again.' : 'PIN verification required'}
            </p>
            {sessionExpired && (
              <p className="text-xs text-slate-500 mt-2">
                Your session has expired. Please enter your PIN again.
              </p>
            )}
          </div>
        </div>
        <PinVerificationModal
          isOpen={showPinModal}
          onClose={handleModalClose}
          onVerify={handlePinVerify}
          title={sessionExpired ? "Session Expired" : "PIN Required"}
          description={sessionExpired 
            ? "Your session has expired. Enter your PIN to continue." 
            : "Enter your PIN to access this report"}
          role={userRole === 'admin' ? 'admin' : 'manager'}
        />
      </>
    );
  }

  return children;
};

export default ProtectedRoute;